import { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { XIcon, UploadIcon, LinkIcon, DocumentIcon } from '@heroicons/react/outline';
import { useDropzone } from 'react-dropzone';

export default function ResourceModal({ isOpen, onClose, onSelect, resource }) {
  // بيانات المورد المرسلة للمودال
  const [selectedTab, setSelectedTab] = useState(0);
  const [file, setFile] = useState(null);
  const [link, setLink] = useState('');
  const [allowDownload, setAllowDownload] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isFree, setIsFree] = useState(false);
  
  // مرجع لتتبع حالة المودال (مفتوح/يغلق)
  const isOpenRef = useRef(isOpen);

  // تحديث المرجع عندما تتغير حالة المودال
  useEffect(() => {
    // تحديث المرجع فقط عندما يكون المودال مفتوح
    if (isOpen) {
      isOpenRef.current = true;
    }
  }, [isOpen]);

  // تحديث البيانات عندما يتغير الـ resource أو حالة المودال
  useEffect(() => {
    // تعيين القيم فقط إذا كان المودال مفتوحًا
    if (isOpen) {
      if (resource) {
        // تعيين بيانات النموذج من المورد
        setSelectedTab(resource.type === 'link' ? 1 : 0);
        setLink(resource.url || '');
        setAllowDownload(resource.allowDownload ?? true);
        setTitle(resource.title || '');
        setDescription(resource.description || '');
        setIsFree(resource.isFree || false);
      } else {
        // إعادة تعيين القيم الافتراضية فقط عند فتح مودال جديد
        setSelectedTab(0);
        setFile(null);
        setLink('');
        setAllowDownload(true);
        setTitle('');
        setDescription('');
        setIsFree(false);
      }
    }
  }, [isOpen, resource]);

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles?.length > 0) {
      const file = acceptedFiles[0];
      // إنشاء معاينة للملف بدون تعديل الكائن الأصلي
      const preview = URL.createObjectURL(file);
      
      // نضيف المعاينة كخاصية منفصلة
      file.preview = preview;
      
      // نستخدم الملف الأصلي مباشرة بدون محاولة تعديل خاصية path
      setFile(file);
      
      // لا نغير العنوان عند اختيار الملف مطلقًا
      // تم إزالة الكود الذي يغير العنوان
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false
  });

  const handleSubmit = (type) => {
    // إذا كان هناك resource موجود، نضيف الـ id بتاعه
    const resourceId = resource?.id;
    const formData = {
      title,
      description,
      allowDownload,
      showInBrowser: true, // دائماً سيتم العرض في المتصفح
      type,
      is_free: isFree || false
    };

    if (type === 'file' && file) {
      // الملف المرسل من ResourceModal
      
      // التأكد من أن الملف موجود ومن نوع File
      if (!(file instanceof File)) {
        // التحقق من نوع الملف
      }
      
      onSelect({
        id: resourceId,
        ...formData,
        file: file, // إضافة الملف نفسه
        fileName: file.name,
        filePath: file.path, // إضافة مسار الملف
        preview: file.preview, // إضافة معاينة الملف
        mimeType: file.type,
        size: file.size
      });
      
      // تنظيف الحالة
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      setFile(null);
      setTitle('');
      setDescription('');
      onClose();
    } else if (type === 'link' && link) {
      onSelect({
        id: resourceId,
        ...formData,
        url: link
      });
      
      // تنظيف الحالة
      setLink('');
      setTitle('');
      setDescription('');
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {
        // تعيين المرجع إلى false عند إغلاق المودال
        isOpenRef.current = false;
        onClose();
      }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-5 shadow-xl transition-all">
                <div className="flex justify-between items-center mb-3">
                  <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                    تعديل الحقيبة التعليمية
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <XIcon className="w-5 h-5" />
                  </button>
                </div>

                <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
                  <Tab.List className="flex space-x-2 rounded-lg bg-gray-100 dark:bg-gray-700/50 p-1 mb-3 rtl:space-x-reverse">
                    <Tab
                      className={({ selected }) =>
                        `w-full rounded-lg py-2.5 text-sm font-medium leading-5 flex items-center justify-center gap-2
                        ${selected
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-white/[0.12] dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                        }`
                      }
                    >
                      <DocumentIcon className="w-5 h-5" />
                      رفع ملف
                    </Tab>
                    <Tab
                      className={({ selected }) =>
                        `w-full rounded-lg py-2.5 text-sm font-medium leading-5 flex items-center justify-center gap-2
                        ${selected
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-white/[0.12] dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                        }`
                      }
                    >
                      
                      <LinkIcon className="w-5 h-5" />
                      رابط خارجي
                    </Tab>
                    
                  </Tab.List>

                  <Tab.Panels>
                    <Tab.Panel>
                      <div className="space-y-2.5">
                        {/* عنوان المحتوى */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            العنوان
                          </label>
                          <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="أدخل عنوان المحتوى"
                          />
                        </div>

                        {/* وصف المحتوى */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            الوصف (اختياري)
                          </label>
                          <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="2"
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="أدخل وصفاً مختصراً"
                          />
                        </div>

                        {/* منطقة رفع الملف */}
                        <div>
                          {resource && resource.url && resource.type !== 'link' && (
                            <div className="mb-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-2">
                                <DocumentIcon className="w-5 h-5 text-blue-500" />
                                <div className="flex-1 overflow-hidden">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    الملف الحالي
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {resource.url.split('/').pop()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          <div
                            {...getRootProps()}
                            className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer
                              ${isDragActive 
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                                : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
                              }`}
                          >
                            <input {...getInputProps()} />
                            <UploadIcon className="mx-auto h-5 w-5 text-gray-400 dark:text-gray-500 mb-0.5" />
                            {file ? (
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                تم اختيار: {file.name}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {resource && resource.type !== 'link' ? 'اختر ملف جديد للاستبدال' : 'اسحب الملف هنا أو اضغط للاختيار'}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* خيارات الملف */}
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setAllowDownload(!allowDownload)}
                              className="relative inline-flex items-center cursor-pointer"
                            >
                              <div
                                className={`block h-6 w-11 rounded-full transition-colors duration-200 ease-in-out ${
                                  allowDownload ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                                }`}
                              />
                              <div
                                className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                  allowDownload ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-700 dark:text-gray-200">
                                السماح بالتحميل
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({allowDownload ? 'يمكن للطلاب تحميل ومشاهدة الملف' : 'يمكن للطلاب مشاهدة الملف فقط'})
                              </span>
                            </div>
                          </div>
                          
                          {/* خيار مجاني */}
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setIsFree(!isFree)}
                              className="relative inline-flex items-center cursor-pointer"
                            >
                              <div
                                className={`block h-6 w-11 rounded-full transition-colors duration-200 ease-in-out ${
                                  isFree ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                                }`}
                              />
                              <div
                                className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                  isFree ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-700 dark:text-gray-200">
                                مجاني (معاينة)
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {isFree ? 'يمكن للجميع رؤية هذه الحقيبة' : 'سيتمكن المشتركون فقط من رؤية هذه الحقيبة'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* زر الإضافة */}
                        <div className="mt-4 flex justify-center">
                          <button
                            onClick={() => handleSubmit('file')}
                            disabled={!file || !title}
                            className="px-6 py-2.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-2/3 mx-auto transition-colors"
                          >
                            إضافة ملف
                          </button>
                        </div>
                      </div>
                    </Tab.Panel>

                    <Tab.Panel>
                      <div className="space-y-2.5">
                        {/* عنوان المحتوى */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            العنوان
                          </label>
                          <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="أدخل عنوان المحتوى"
                          />
                        </div>

                        {/* وصف المحتوى */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            الوصف (اختياري)
                          </label>
                          <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="2"
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="أدخل وصفاً مختصراً"
                          />
                        </div>

                        {/* حقل الرابط */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            الرابط
                          </label>
                          <input
                            type="url"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="https://..."
                            dir="ltr"
                          />
                        </div>

                        {/* خيار مجاني */}
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            type="button"
                            onClick={() => setIsFree(!isFree)}
                            className="relative inline-flex items-center cursor-pointer"
                          >
                            <div
                              className={`block h-6 w-11 rounded-full transition-colors duration-200 ease-in-out ${
                                isFree ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                              }`}
                            />
                            <div
                              className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                isFree ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 dark:text-gray-200">
                              مجاني (معاينة)
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {isFree ? 'يمكن للجميع رؤية هذه الحقيبة' : 'سيتمكن المشتركون فقط من رؤية هذه الحقيبة'}
                            </span>
                          </div>
                        </div>

                        {/* زر الإضافة */}
                        <div className="mt-4 flex justify-center">
                          <button
                            onClick={() => handleSubmit('link')}
                            disabled={!link || !title}
                            className="px-6 py-2.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-2/3 mx-auto transition-colors"
                          >
                            إضافة رابط
                          </button>
                        </div>
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
